import { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import queryString from "query-string";

import {
  QUERY_WIDTHS,
  TABLE_ASSETS_COLUMNS,
  TABLE_DISPOSED_ASSETS_COLUMNS,
  ASSETS_PER_PAGE,
} from "@utils/index";
import { AssetModel } from "@interfaces/index";
import { useCommonStore, useUserStore } from "@stores/index";
import { useAssetGetBusinessAssets, useAssetGetOne } from "@providers/index";
import { useNotify } from "@hooks/index";
import {
  PageContentContainer,
  Table,
  TableHead,
  TableBody,
  TrNoItems,
  TableTabs,
  TableRowAsset,
  Pagination,
  BusinessSelector,
  RangePicker,
  SquareRoundedPlusSvg,
  Button,
  AssetTypeSelector,
  DesktopPageTitle,
  TutorialButton,
} from "@components/index";
import { AssetForm, AssetDetails } from "@pages/index";

export const AssetsTable = () => {
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });
  const location = useLocation();
  const idValue = location.pathname.split("/").slice(-1)[0];
  const navigate = useNavigate();
  const notify = useNotify();
  const setState = useCommonStore(({ setState }) => setState);

  const businesses = useUserStore(({ businesses }) => businesses);
  const defaultBusiness = useUserStore(({ business }) => business);
  const [businessId, setBusinessId] = useState("");

  const [activeTab, setActiveTab] = useState("");
  const [typeId, setTypeId] = useState("");
  const [assets, setAssets] = useState<AssetModel[]>();
  const [selectedPage, setSelectedPage] = useState(1);

  const [redirectNeeds, setRedirectNeeds] = useState(false);

  const [isDetails, setIsDetails] = useState("");
  const [isUpdate, setIsUpdate] = useState("");
  const [asset, setAsset] = useState<AssetModel>();

  const filteredAssets = useMemo(
    () =>
      assets?.length
        ? assets.filter(
            ({ disposal, type }) =>
              (activeTab === "Disposal history" ? !!disposal : !disposal) &&
              (typeId ? typeId === type : true),
          )
        : [],
    [assets, activeTab, typeId],
  );

  const availableTutorialNames = useMemo(() => {
    const arr = ["assetCreateTutorial"];
    if (assets?.length) {
      arr.push("assetEditTutorial");
      arr.push("assetDisposeTutorial");
    }
    return arr;
  }, [assets]);

  useEffect(() => {
    const params = queryString.parse(location.search);
    setBusinessId(
      (params?.businessId as string) ||
        defaultBusiness?.uniqueId ||
        businesses[0]?.uniqueId ||
        "",
    );
    setTypeId((params?.typeId as string) || "");
    setActiveTab((params?.activeTab as string) || "Assets");
    setSelectedPage(parseInt((params?.page || "1").toString()));
    if (idValue && idValue !== "assets") {
      setIsDetails(idValue);
    }
  }, [location, defaultBusiness, businesses, idValue]);

  const {
    isPending: isPendingGetAll,
    isSuccess: isSuccessGetAll,
    isStale: isStaleGetAll,
    data: dataGetAll,
    isError: isErrorGetAll,
    error: errorGetAll,
  } = useAssetGetBusinessAssets(businessId);

  useEffect(() => {
    if (isSuccessGetAll && dataGetAll) {
      setAssets(dataGetAll);
    }
  }, [isSuccessGetAll, dataGetAll]);

  useEffect(() => {
    if (isErrorGetAll && errorGetAll) {
      notify("error", errorGetAll.message);
    }
  }, [isErrorGetAll, errorGetAll, notify]);

  const {
    isPending: isPendingGetOne,
    isSuccess: isSuccessGetOne,
    isStale: isStaleGetOne,
    data: dataGetOne,
    isError: isErrorGetOne,
    error: errorGetOne,
  } = useAssetGetOne(
    isDetails || (isUpdate && isUpdate !== "new" ? isUpdate : ""),
  );

  useEffect(() => {
    if (isErrorGetOne && errorGetOne) {
      notify("error", errorGetOne.message);
    }
  }, [isErrorGetOne, errorGetOne, notify]);

  useEffect(() => {
    if (isSuccessGetOne && dataGetOne) {
      setAsset(dataGetOne);
    }
  }, [isSuccessGetOne, dataGetOne]);

  useEffect(() => {
    setState({
      isLoading:
        (isStaleGetAll && isPendingGetAll) ||
        (isStaleGetOne && isPendingGetOne),
    });
  }, [
    isStaleGetAll,
    isPendingGetAll,
    isStaleGetOne,
    isPendingGetOne,
    setState,
  ]);

  useEffect(() => {
    if (!redirectNeeds) {
      return;
    }
    setRedirectNeeds(false);
    const params = queryString.stringify(
      {
        businessId,
        activeTab,
        typeId,
        page: selectedPage,
      },
      { skipEmptyString: true },
    );
    navigate(`?${params}`);
  }, [redirectNeeds, navigate, businessId, typeId, activeTab, selectedPage]);

  const handleSelectBusiness = (businessId: string) => {
    setRedirectNeeds(true);
    setSelectedPage(1);
    setBusinessId(businessId);
    setTypeId("");
  };

  const handleSelectTab = (value: string) => {
    setRedirectNeeds(true);
    setSelectedPage(1);
    setActiveTab(value);
  };

  const handleSelectType = (typeId: string) => {
    setRedirectNeeds(true);
    setSelectedPage(1);
    setTypeId(typeId);
  };

  const handleSelectPage = (page: number) => {
    setRedirectNeeds(true);
    setSelectedPage(page);
  };

  const handleCloseDrawer = () => {
    setIsDetails("");
    setIsUpdate("");
    setAsset(undefined);
    if (idValue && idValue !== "assets") {
      navigate("/assets");
    }
  };

  const handleEditAsset = (id: string) => {
    setIsDetails("");
    setIsUpdate(id);
  };

  return (
    <div className="md:pt-6">
      {!isMobile && (
        <DesktopPageTitle
          title="Assets"
          tutorialNames={availableTutorialNames}
          faqCategory={["Assets Guide"]}
        />
      )}
      <div className="flex flex-col justify-between gap-y-4 px-4 pt-4 md:flex-row md:px-10 md:pt-6">
        <BusinessSelector
          height="small"
          width="w-full md:w-64"
          value={businessId}
          onSetBusiness={handleSelectBusiness}
          dataTestid="filter_businessSelector"
        />
        <RangePicker
          startDate={""}
          endDate={""}
          prefix="assetsTable"
          onSetStartDate={() => {}}
          onSetEndDate={() => {}}
          showPeriods={false}
          disabled
          dataTestid="filter_dateRangeSelector"
        />
      </div>
      <PageContentContainer>
        <div className="flex flex-col-reverse justify-between gap-y-6 md:flex-row">
          <TableTabs
            tabs={["Assets", "Disposal history"]}
            activeTab={activeTab}
            dataTestid="tabs_assetsTable"
            onClick={(value) => handleSelectTab(value)}
          />
          <div className="mt-2 flex w-full flex-col gap-4 md:mt-0 md:w-auto md:flex-row">
            <div className="w-full md:w-[230px]">
              <AssetTypeSelector
                height="small"
                businessId={businessId}
                value={typeId}
                onSetAssetType={handleSelectType}
              />
            </div>
            <Button
              title="Add Asset"
              leftIcon={<SquareRoundedPlusSvg />}
              className="w-full md:w-fit md:px-5"
              height="small"
              variant="secondary"
              dataTestid="button_addAsset"
              onClick={() => setIsUpdate("new")}
            />
          </div>
          {isMobile && (
            <TutorialButton
              variant="dark"
              tutorialNames={availableTutorialNames}
              faqCategory={["Assets Guide"]}
            />
          )}
        </div>
        <Table
          className="pb-4 pt-6 [&>table]:border-collapse"
          dataTestid="table_assetsTable"
        >
          <TableHead
            columns={
              activeTab === "Disposal history"
                ? TABLE_DISPOSED_ASSETS_COLUMNS
                : TABLE_ASSETS_COLUMNS
            }
            className="border-b-black-16"
            dataTestid="thead_assetsTable"
          />
          <TableBody dataTestid="tbody_assetsTable">
            {filteredAssets.length ? (
              filteredAssets
                .slice(
                  Math.round((selectedPage - 1) * ASSETS_PER_PAGE),
                  Math.round(selectedPage * ASSETS_PER_PAGE),
                )
                .map((asset) => (
                  <TableRowAsset
                    key={asset.uniqueId}
                    asset={asset}
                    showDisposal={activeTab === "Disposal history"}
                    onDetails={() => setIsDetails(asset.uniqueId)}
                  />
                ))
            ) : (
              <TrNoItems title="No assets found" />
            )}
          </TableBody>
        </Table>
        {filteredAssets.length > 0 && (
          <Pagination
            count={filteredAssets.length}
            perPage={ASSETS_PER_PAGE}
            currentPage={selectedPage}
            onSelect={handleSelectPage}
          />
        )}
        {(isUpdate === "new" || (isUpdate && asset)) && (
          <AssetForm
            isUpdating={isUpdate !== "new"}
            initialBusinessId={businessId}
            asset={asset}
            onClose={handleCloseDrawer}
          />
        )}
        {isDetails && (
          <AssetDetails
            asset={asset}
            onEdit={() => handleEditAsset(asset!.uniqueId)}
            onClose={handleCloseDrawer}
          />
        )}
      </PageContentContainer>
    </div>
  );
};
